<style scoped lang="scss">
.app-container {
	display: flex;
	height: 100vh;
}

.maintenance-banner {
	background-color: $danger;
	color: #fff;
	border-radius: 0.375rem;
	margin-bottom: 0.75rem;

	.banner-icon {
		font-size: 1rem;
		flex-shrink: 0;
	}

	.banner-label {
		font-size: 0.875rem;
	}

	.banner-chip {
		background-color: rgba(255, 255, 255, 0.92);
		color: $danger;
		font-size: 0.75rem;
	}

	.banner-message {
		font-size: 0.8125rem;
		opacity: 0.9;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
}
</style>
<template>
	<div class="app-container">
		<template v-if="session.isLoading">Loading...</template>
		<template v-else-if="showLockout">
			<MaintenanceScreen
				:message="syncGate?.message ?? null"
				:origin="syncGate?.origin ?? null"
				:started-at="syncGate?.startedAt ?? null"
			/>
		</template>
		<template v-else-if="session.error">Error: {{ session.error }}</template>
		<template v-else>
			<Sidebar />
			<MainContent>
				<div
					v-if="showRootBanner"
					class="maintenance-banner d-flex align-items-center gap-2 px-3 py-2"
				>
					<i class="bi bi-exclamation-triangle-fill banner-icon"></i>
					<span class="banner-label fw-semibold flex-shrink-0"
						>Maintenance active:</span
					>
					<span
						v-for="entry in activeGates"
						:key="entry.host"
						class="badge banner-chip text-start flex-shrink-0"
					>
						{{ entry.host }} · origin: {{ entry.gate.origin }}
					</span>
					<span v-if="activeGateMessages" class="banner-message">
						{{ activeGateMessages }}
					</span>
					<button
						type="button"
						class="btn-close btn-close-white ms-auto flex-shrink-0"
						aria-label="Dismiss maintenance notice"
						@click="bannerDismissed = true"
					></button>
				</div>
				<router-view v-slot="{ Component }">
					<component :is="Component" />
				</router-view>
			</MainContent>
			<UpdateNotification :updateDate="updateDate" :isRevert="isRevert" />
		</template>
	</div>
</template>
<script setup lang="ts">
import * as axios from "axios";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";

import Sidebar from "@/components/layout/Sidebar.vue";
import MainContent from "@/components/layout/MainContent.vue";
import UpdateNotification from "@/components/UpdateNotification.vue";
import MaintenanceScreen from "@/view/maintenance/MaintenanceScreen.vue";
import { SSEManager, type SSEMessageStatus } from "@/SSEManager";
import { AxiosErrorJSON } from "@/client";
import type { MaintenanceGate, MaintenanceHost } from "@/type/api";
import { router } from "@/route/config";
import { useSessionStore } from "@/store/session";

const revision = ref<string>("");
const buildTime = ref<string | null>(null);
const session = useSessionStore();
const updateDate = ref<Date | null>(null);
const isRevert = ref(false);
const seenStatus = ref(false);
const bannerDismissed = ref(false);
let statusSubscription: string | null = null;

// The sync gate's current state (null until the store has fetched it).
const syncGate = computed<MaintenanceGate | null>(
	() => session.maintenance?.gates.sync ?? null,
);

// Non-root users are locked out of the whole dashboard while the sync gate is
// armed. An unknown role (the gate 503'd the session refresh on mount, so
// session.self never loaded) is treated as non-root — the gate only lets root
// sessions through.
const isRoot = computed(() => session.self?.role === "root");
const showLockout = computed(
	() => session.maintenance?.gates.sync.active === true && !isRoot.value,
);

// The root banner shows one chip per armed host, then any operator messages.
const activeGates = computed(() => {
	const m = session.maintenance;
	if (!m) return [];
	const list: { host: MaintenanceHost; gate: MaintenanceGate }[] = [];
	if (m.gates.sync.active) list.push({ host: "sync", gate: m.gates.sync });
	if (m.gates.rmo.active) list.push({ host: "rmo", gate: m.gates.rmo });
	return list;
});
const showRootBanner = computed(
	() => isRoot.value && activeGates.value.length > 0 && !bannerDismissed.value,
);
const activeGateMessages = computed(() =>
	activeGates.value
		.map((entry) => entry.gate.message)
		.filter((message): message is string => message !== null)
		.join(" — "),
);

// A deployment whose build time is earlier than the previously observed build
// means the server has been rolled back to an older build.
function isBuildTimeOlder(previous: string | null, current: string): boolean {
	if (previous == null) return false;
	const prev = Date.parse(previous);
	const curr = Date.parse(current);
	if (Number.isNaN(prev) || Number.isNaN(curr)) return false;
	return curr < prev;
}

// Keep the dismissible root banner armed whenever the active-gate set changes
// (a host arms, clears, or its origin flips) so a fresh notice is never hidden.
watch(
	() =>
		activeGates.value
			.map((entry) => `${entry.host}:${entry.gate.origin}`)
			.join(","),
	() => {
		bannerDismissed.value = false;
	},
);

function redirectToSignin(): void {
	console.log(
		"root session not loaded, user is not authenticated",
		router.currentRoute.value.fullPath,
	);
	router.push(`/signin?next=${router.currentRoute.value.fullPath}`);
}

onMounted(() => {
	statusSubscription = SSEManager.subscribeStatus((msg: SSEMessageStatus) => {
		console.log("status update:", msg);
		if (msg.status == "connected") {
			if (!seenStatus.value) {
				// First connection of this page session: record the baseline.
				seenStatus.value = true;
				revision.value = msg.revision;
				buildTime.value = msg.build_time;
			} else if (msg.revision !== revision.value) {
				// A different revision was deployed.
				isRevert.value = isBuildTimeOlder(buildTime.value, msg.build_time);
				revision.value = msg.revision;
				buildTime.value = msg.build_time;
				updateDate.value = new Date();
			}
		} else if (msg.status == "shutdown") {
			console.log(
				"server is shutting down, waiting a bit and re-initiating connection",
			);
			SSEManager.reconnect(5);
		} else {
			console.error("unrecognized server status", msg.status);
		}
	});
	session
		.get()
		.then(() => {
			// Session established: probe the maintenance gates so the shell can
			// swap to the lockout screen (non-root) or show the root banner.
			// The probe tolerates 401/503 without throwing; SSE "maintenance"
			// events keep the store fresh afterwards.
			console.log("session loaded by Authenticated");
			session.fetchMaintenance().catch(() => {});
		})
		.catch((e: unknown) => {
			const status =
				e instanceof axios.AxiosError
					? ((e.toJSON() as AxiosErrorJSON).status ?? 0)
					: 0;
			if (status == 503) {
				// The sync gate answers 503 on the gated endpoints — including
				// the session refresh — for non-root sessions. GET
				// /api/maintenance is whitelisted from the gate, so probe it:
				// when the session cookie is still valid it reports the armed
				// gate and the shell can render the lockout screen instead of
				// bouncing to /signin.
				session
					.fetchMaintenance()
					.then(() => {
						if (session.maintenance?.gates.sync.active !== true) {
							redirectToSignin();
						}
						// else: showLockout flips the template to the maintenance
						// screen; the router guard keeps the current route.
					})
					.catch(() => {
						redirectToSignin();
					});
				return;
			}
			redirectToSignin();
		});
});
onUnmounted(() => {
	if (statusSubscription !== null) {
		SSEManager.unsubscribe(statusSubscription);
	}
});
</script>
